import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  MessageSquare, Trash2, X, Mail, Phone, User,
  Search, Check, Clock, Calendar, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        headers: getAuthHeaders()
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/contacts/${id}/status?status=${newStatus}`, {}, {
        headers: getAuthHeaders()
      });
      fetchContacts();
      if (selectedContact && selectedContact.id === id) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato?')) return;

    try {
      await axios.delete(`${API}/contacts/${id}`, {
        headers: getAuthHeaders()
      });
      setSelectedContact(null);
      fetchContacts();
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
      alert('Erro ao excluir contato');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const pendingCount = contacts.filter(c => c.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Contatos</h1>
          <p className="text-gray-400">
            Mensagens recebidas pelo site
            {pendingCount > 0 && (
              <span className="ml-2 text-yellow-500">({pendingCount} pendentes)</span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, email ou assunto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#00F0FF]"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'answered'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === status 
                  ? 'bg-[#00F0FF] text-black' 
                  : 'bg-[#0a0a0a] text-gray-400 border border-white/10 hover:border-white/30'
              }`}
            >
              {status === 'all' ? 'Todos' : status === 'pending' ? 'Pendentes' : 'Respondidos'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquare className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">Nenhum contato encontrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contacts List */}
          <div className="lg:col-span-1 space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {filteredContacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  selectedContact?.id === contact.id 
                    ? 'bg-[#0a0a0a] border-[#00F0FF]' 
                    : 'bg-[#0a0a0a] border-white/5 hover:border-white/10'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white font-medium truncate flex-1">{contact.name}</h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    contact.status === 'pending' 
                      ? 'bg-yellow-500/20 text-yellow-500' 
                      : 'bg-green-500/20 text-green-500'
                  }`}>
                    {contact.status === 'pending' ? 'Pendente' : 'Respondido'}
                  </span>
                </div>
                <p className="text-gray-400 text-sm truncate mb-1">{contact.subject}</p>
                <p className="text-gray-600 text-xs">{formatDate(contact.created_at)}</p>
              </div>
            ))}
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-2">
            {selectedContact ? (
              <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-6 pb-6 border-b border-white/10">
                  <div>
                    <h2 className="text-xl font-bold text-white mb-2">{selectedContact.name}</h2>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <a 
                        href={`mailto:${selectedContact.email}`}
                        className="text-[#00F0FF] hover:underline flex items-center gap-1"
                      >
                        <Mail size={14} />
                        {selectedContact.email}
                      </a>
                      {selectedContact.phone && (
                        <a 
                          href={`tel:${selectedContact.phone}`}
                          className="text-green-400 hover:underline flex items-center gap-1"
                        >
                          <Phone size={14} />
                          {selectedContact.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(selectedContact.id)}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#111] p-4 rounded-lg">
                    <p className="text-gray-500 text-xs mb-1">Assunto</p>
                    <p className="text-white font-medium">{selectedContact.subject}</p>
                  </div>
                  <div className="bg-[#111] p-4 rounded-lg">
                    <p className="text-gray-500 text-xs mb-1">Tipo de Contratação</p>
                    <p className="text-white font-medium">
                      {selectedContact.contract_type === 'orgao_publico' && 'Órgão Público / Prefeitura'}
                      {selectedContact.contract_type === 'festa_cidade' && 'Festa da Cidade'}
                      {selectedContact.contract_type === 'festival_publico' && 'Festival Público'}
                      {selectedContact.contract_type === 'evento_corporativo' && 'Evento Corporativo'}
                      {selectedContact.contract_type === 'festa_particular' && 'Festa Particular'}
                      {selectedContact.contract_type === 'outro' && 'Outro'}
                      {!selectedContact.contract_type && 'Não informado'}
                    </p>
                  </div>
                  <div className="bg-[#111] p-4 rounded-lg col-span-2">
                    <p className="text-gray-500 text-xs mb-1">Detalhes da Contratação</p>
                    <p className="text-[#00F0FF] font-medium">{selectedContact.contract_details || 'Não informado'}</p>
                  </div>
                  <div className="bg-[#111] p-4 rounded-lg">
                    <p className="text-gray-500 text-xs mb-1">Tipo de Evento</p>
                    <p className="text-white font-medium">{selectedContact.event_type || 'Não informado'}</p>
                  </div>
                  <div className="bg-[#111] p-4 rounded-lg">
                    <p className="text-gray-500 text-xs mb-1">Recebido em</p>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Calendar size={14} />
                      {formatDate(selectedContact.created_at)}
                    </p>
                  </div>
                  <div className="bg-[#111] p-4 rounded-lg col-span-2">
                    <p className="text-gray-500 text-xs mb-1">Status</p>
                    <p className={`font-medium flex items-center gap-2 ${
                      selectedContact.status === 'pending' ? 'text-yellow-500' : 'text-green-500'
                    }`}>
                      {selectedContact.status === 'pending' ? <Clock size={14} /> : <Check size={14} />}
                      {selectedContact.status === 'pending' ? 'Pendente' : 'Respondido'}
                    </p>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <h3 className="text-gray-400 text-sm mb-3">Mensagem</h3>
                  <div className="bg-[#111] p-4 rounded-lg">
                    <p className="text-white whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <a
                    href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00F0FF] to-[#FF0099] rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                  >
                    <Mail size={18} />
                    Responder por Email
                  </a>
                  {selectedContact.status === 'pending' ? (
                    <button
                      onClick={() => handleStatusChange(selectedContact.id, 'answered')}
                      className="px-6 py-3 border border-green-500 text-green-500 rounded-lg font-medium flex items-center gap-2 hover:bg-green-500/10 transition-colors"
                    >
                      <Check size={18} />
                      Marcar Respondido
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatusChange(selectedContact.id, 'pending')}
                      className="px-6 py-3 border border-yellow-500 text-yellow-500 rounded-lg font-medium flex items-center gap-2 hover:bg-yellow-500/10 transition-colors"
                    >
                      <Clock size={18} />
                      Marcar Pendente
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-12 text-center">
                <MessageSquare className="mx-auto mb-4 text-gray-600" size={48} />
                <p className="text-gray-400">Selecione um contato para ver os detalhes</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
